const chapter = {
  id: 'biomolecules',
  slug: 'biomolecules',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Biomolecules',
        hindi: 'जैव अणु',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Easy-Medium',
        hindi: 'आसान-मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Organic / Biochemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'carbohydrates',
      title: {
        english: 'Carbohydrates',
        hindi: 'कार्बोहाइड्रेट',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Carbohydrate',
        hindi: 'कार्बोहाइड्रेट',
          },
          body: {
            english: 'Polyhydroxy aldehyde (aldose) or ketone (ketose), or compound that yields these on hydrolysis. General formula $C_n(H_2O)_n$ (not strict).',
        hindi: 'पॉलीहाइड्रॉक्सी एल्डिहाइड (एल्डोज़) या कीटोन (कीटोज़), या यौगिक जो हाइड्रोलिसिस पर इन्हें उत्पन्न करता है। सामान्य सूत्र $C_n(H_2O)_n$ (सख्त नहीं)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Classification by hydrolysis:',
        hindi: 'हाइड्रोलिसिस द्वारा वर्गीकरण:',
            },
            {
              english: '• **Monosaccharides** — cannot be hydrolysed (glucose, fructose, ribose, galactose).',
        hindi: '• **मोनोसेकेराइड** - हाइड्रोलाइज्ड नहीं किया जा सकता (ग्लूकोज, फ्रुक्टोज, राइबोज, गैलेक्टोज)।',
            },
            {
              english: '• **Oligosaccharides** — 2-10 units (sucrose, maltose, lactose).',
        hindi: '• **ओलिगोसेकेराइड्स** - 2-10 इकाइयाँ (सुक्रोज़, माल्टोज़, लैक्टोज़)।',
            },
            {
              english: '• **Polysaccharides** — many units (starch, cellulose, glycogen).',
        hindi: '• **पॉलीसेकेराइड** - कई इकाइयाँ (स्टार्च, सेलूलोज़, ग्लाइकोजन)।',
            },
            {
              english: 'Reducing vs non-reducing: those with free CHO/anomeric C reduce Tollens/Fehling. Sucrose is **non-reducing** (both anomeric C tied up). Maltose and lactose are reducing.',
        hindi: 'कम करना बनाम गैर कम करना: जिनके पास मुफ़्त CHO/anomeric C है वे Tollens/Fehling कम करते हैं। सुक्रोज **नॉन-रिड्यूसिंग** है (दोनों एनोमेरिक सी बंधे हुए हैं)। माल्टोज़ और लैक्टोज़ कम हो रहे हैं। **ग्लूकोज** (D-(+)-ग्लूकोज, CHO/anomeric):',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Glucose** (D-(+)-glucose, $C_6H_{12}O_6$):',
        hindi: '**ग्लूकोज़** (D-(+)-ग्लूकोज़, $C_6H_{12}O_6$):',
            },
            {
              english: '• Open-chain: aldohexose, 4 chiral C, 16 stereoisomers.',
        hindi: '• ओपन-चेन: एल्डोहेक्सोज़, 4 चिरल सी, 16 स्टीरियोइसोमर्स।',
            },
            {
              english: '• Cyclic: pyranose (6-membered, more stable). $\\alpha$ (-OH down) and $\\beta$ (-OH up) anomers at C-1.',
        hindi: '• चक्रीय: पायरानोज़ (6-सदस्यीय, अधिक स्थिर)। C-1 पर $\\alpha$ (-OH नीचे) और $\\beta$ (-OH ऊपर) एनोमर्स।',
            },
            {
              english: '• Mutarotation: $\\alpha$-D-glucose ([α] = +112°) ↔ open ↔ $\\beta$ ([α] = +19°); equilibrium [α] = +52.7°.',
        hindi: '• उत्परिवर्तन: $\\alpha$-D-ग्लूकोज ([α] = +112°) ↔ खुला ↔ $\\beta$ ([α] = +19°); संतुलन [α] = +52.7°.',
            },
            {
              english: '• Reactions: HCN → cyanohydrin; NH₂OH → oxime; reduction (Ni/H₂) → sorbitol; oxidation by Br₂/H₂O → gluconic acid; HNO₃ → saccharic (glucaric) acid; **does NOT react with NaHSO₃ or Schiff** ⇒ –CHO is partly hidden as cyclic hemiacetal.',
        hindi: '• प्रतिक्रियाएं: एचसीएन → सायनोहाइड्रिन; NH₂OH → ऑक्सीम; कमी (Ni/H₂) → सोर्बिटोल; Br₂/H₂O → ग्लूकोनिक एसिड द्वारा ऑक्सीकरण; HNO₃ → सैकेरिक (ग्लुकेरिक) एसिड; **NaHSO₃ या Schiff के साथ प्रतिक्रिया नहीं करता** ⇒ -CHO आंशिक रूप से चक्रीय हेमिसिएटल के रूप में छिपा हुआ है।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Fructose** — ketohexose; in furanose (5-membered) form. Reduces Tollens because in alkaline medium it tautomerises to aldoses (Lobry de Bruyn rearrangement).',
        hindi: '**फ्रुक्टोज**-कीटोहेक्सोज; फ़्यूरानोज़ (5-सदस्यीय) रूप में। टोलेंस को कम करता है क्योंकि क्षारीय माध्यम में यह एल्डोसेस (लॉब्री डी ब्रुइन पुनर्व्यवस्था) में परिवर्तित हो जाता है।',
            },
            {
              english: '**Sucrose** = glucose + fructose linked $\\alpha\\text{-}1,2$ glycosidic; non-reducing. Hydrolysis = **inversion** (specific rotation $+66.5° \\to -39.7°$); product = invert sugar.',
        hindi: '**सुक्रोज** = ग्लूकोज + फ्रुक्टोज लिंक्ड $\\alpha\\text{-}1,2$ ग्लाइकोसिडिक; गैर कम करने वाला. Hydrolysis = **inversion** (specific rotation ); product = invert sugar.',
            },
            {
              english: '**Maltose** = 2 glucose ($\\alpha$-1,4); **Lactose** = galactose + glucose ($\\beta$-1,4).',
        hindi: '**माल्टोज़** = 2 ग्लूकोज ($\\alpha$-1,4); **लैक्टोज** = गैलेक्टोज + ग्लूकोज ($\\beta$-1,4)।',
            },
            {
              english: '**Starch** = amylose (linear $\\alpha$-1,4) + amylopectin (branched, $\\alpha$-1,4 + $\\alpha$-1,6 branches).',
        hindi: '**स्टार्च** = एमाइलोज़ (रैखिक $\\alpha$-1,4) + एमाइलोपेक्टिन (शाखाकृत, $\\alpha$-1,4 + $\\alpha$-1,6 शाखाएँ)।',
            },
            {
              english: '**Cellulose** = linear $\\beta$-1,4 D-glucose; humans cannot digest (no cellulase).',
        hindi: '**सेल्युलोज** = रैखिक $\\beta$-1,4 डी-ग्लूकोज; मनुष्य इसे पचा नहीं सकते (कोई सेल्यूलेज़ नहीं)।',
            },
            {
              english: '**Glycogen** = animal starch; highly branched.',
        hindi: '**ग्लाइकोजन** = पशु स्टार्च; अत्यधिक शाखायुक्त.',
            },
          ],
        },
      ],
    },
    {
      id: 'amino-acids-proteins',
      title: {
        english: 'Amino Acids & Proteins',
        hindi: 'ऐमीनो अम्ल और प्रोटीन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**α-amino acid**: $H_2N$-CHR-COOH. 20 standard; 10 essential (cannot be synthesised — Val, Leu, Ile, Phe, Trp, Met, Thr, Lys, His, Arg).',
        hindi: '**α-अमीनो एसिड**: $H_2N$-CHR-COOH। 20 मानक; 10 आवश्यक (संश्लेषित नहीं किया जा सकता - वैल, ल्यू, इले, फे, टीआरपी, मेट, थ्र, लिस, हिज, आर्ग)।',
            },
            {
              english: 'Exist as **zwitterion** $H_3N^+$-CHR-COO⁻ in solid and at isoelectric pH (pI). Amphoteric.',
        hindi: 'ठोस में और आइसोइलेक्ट्रिक pH (pI) पर **zwitterion** $H_3N^+$-CHR-COO⁻ के रूप में मौजूद होते हैं। उभयधर्मी।',
            },
            {
              english: 'pI: average of relevant pKa\'s; at pI net charge = 0, lowest solubility, no migration in electric field.',
        hindi: 'pI: प्रासंगिक pKa का औसत; पीआई नेट charge = 0 पर, सबसे कम घुलनशीलता, विद्युत क्षेत्र में कोई प्रवासन नहीं। **पेप्टाइड बॉन्ड** = -CO-NH- (एमाइड)। डाइपेप्टाइड → पॉलीपेप्टाइड → प्रोटीन।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Peptide bond** = -CO-NH- (amide). Dipeptide → polypeptide → protein.',
        hindi: '**पेप्टाइड बॉन्ड** = -CO-NH- (एमाइड)। डाइपेप्टाइड → पॉलीपेप्टाइड → प्रोटीन।',
            },
            {
              english: 'Structure levels:',
        hindi: 'संरचना स्तर:',
            },
            {
              english: '• **Primary** — sequence of amino acids.',
        hindi: '• **प्राथमिक** - अमीनो एसिड का अनुक्रम।',
            },
            {
              english: '• **Secondary** — α-helix or β-pleated sheet (H-bonds between -CO and -NH of backbone).',
        hindi: '• **माध्यमिक** - α-हेलिक्स या β-प्लीटेड शीट (रीढ़ की हड्डी के -CO और -NH के बीच H-बॉन्ड)।',
            },
            {
              english: '• **Tertiary** — overall 3-D fold (H-bond, S-S bridges, hydrophobic, ionic).',
        hindi: '• **तृतीयक** - समग्र 3-डी फोल्ड (एच-बॉन्ड, एस-एस ब्रिज, हाइड्रोफोबिक, आयनिक)।',
            },
            {
              english: '• **Quaternary** — assembly of subunits (haemoglobin: 2α + 2β).',
        hindi: '• **चतुर्धातुक** - उपइकाइयों का संयोजन (हीमोग्लोबिन: 2α + 2β)। **विकृतीकरण** — 2°/3°/4° (1° अक्षुण्ण) की हानि। कारण: गर्मी, यूरिया, भारी धातुएँ। अंडे की सफेदी का जमाव; दूध फटना।',
            },
            {
              english: '**Denaturation** — loss of 2°/3°/4° (1° intact). Causes: heat, acid/base, urea, heavy metals. Egg-white coagulation; curdling of milk.',
        hindi: '**विकृतीकरण** - 2°/3°/4° (1° अक्षुण्ण) की हानि। कारण: गर्मी, acid/base, यूरिया, भारी धातुएँ। अंडे की सफेदी का जमाव; दूध फटना।',
            },
            {
              english: 'Fibrous (insoluble — keratin, collagen) vs Globular (soluble — albumin, haemoglobin).',
        hindi: 'रेशेदार (अघुलनशील - केराटिन, कोलेजन) बनाम गोलाकार (घुलनशील - एल्ब्यूमिन, हीमोग्लोबिन)।',
            },
          ],
        },
      ],
    },
    {
      id: 'enzymes',
      title: {
        english: 'Enzymes & Hormones',
        hindi: 'एन्ज़ाइम और हार्मोन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Enzymes — biological protein catalysts; **highly specific** (lock-and-key); active at body T, definite pH.',
        hindi: 'एंजाइम - जैविक प्रोटीन उत्प्रेरक; **अत्यधिक विशिष्ट** (ताला और चाबी); शरीर टी पर सक्रिय, निश्चित पीएच। उदाहरण: एमाइलेज़ (स्टार्च → माल्टोज़), माल्टेज़ (माल्टोज़ → ग्लूकोज), पेप्सिन (प्रोटीन → पेट में पेप्टाइड्स), ट्रिप्सिन (आंत में), यूरियाज़। **हार्मोन** - रासायनिक संदेशवाहक (स्टेरॉयड: एस्ट्रोजन, टेस्टोस्टेरोन; पेप्टाइड: इंसुलिन; अमीनो-एसिड व्युत्पन्न: थायरोक्सिन, एड्रेनालाईन)।',
            },
            {
              english: 'Examples: amylase (starch → maltose), maltase (maltose → glucose), pepsin (protein → peptides in stomach), trypsin (in intestine), urease.',
        hindi: 'उदाहरण: एमाइलेज़ (स्टार्च → माल्टोज़), माल्टेज़ (माल्टोज़ → ग्लूकोज), पेप्सिन (प्रोटीन → पेट में पेप्टाइड्स), ट्रिप्सिन (आंत में), यूरेस।',
            },
            {
              english: '**Hormones** — chemical messengers (steroid: estrogen, testosterone; peptide: insulin; amino-acid derived: thyroxine, adrenaline).',
        hindi: '**हार्मोन** - रासायनिक संदेशवाहक (स्टेरॉयड: एस्ट्रोजन, टेस्टोस्टेरोन; पेप्टाइड: इंसुलिन; अमीनो-एसिड व्युत्पन्न: थायरोक्सिन, एड्रेनालाईन)।',
            },
            {
              english: 'Insulin (pancreas) lowers blood glucose; glucagon raises it. Diabetes mellitus = insulin deficiency.',
        hindi: 'इंसुलिन (अग्न्याशय) रक्त शर्करा को कम करता है; ग्लूकागन इसे बढ़ाता है। मधुमेह mellitus = insulin deficiency।',
            },
          ],
        },
      ],
    },
    {
      id: 'vitamins',
      title: {
        english: 'Vitamins',
        hindi: 'विटामिन',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Vitamin', 'Soluble in', 'Source', 'Deficiency'],
          rows: [
            ['A (retinol)', 'Fat', 'Carrots, fish liver oil', 'Night blindness'],
            ['B₁ (thiamine)', 'Water', 'Yeast, milk', 'Beri-beri'],
            ['B₂ (riboflavin)', 'Water', 'Egg, milk', 'Cheilosis, glossitis'],
            ['B₆ (pyridoxine)', 'Water', 'Cereals, yeast', 'Convulsions'],
            ['B₁₂ (cobalamin)', 'Water', 'Meat, eggs', 'Pernicious anaemia'],
            ['C (ascorbic)', 'Water', 'Citrus fruits', 'Scurvy'],
            ['D (calciferol)', 'Fat', 'Fish, sunlight', 'Rickets, osteomalacia'],
            ['E (tocopherol)', 'Fat', 'Vegetable oils', 'Muscular weakness'],
            ['K', 'Fat', 'Green leafy', 'Bleeding (poor clotting)'],
          ],
        },
      ],
    },
    {
      id: 'nucleic-acids',
      title: {
        english: 'Nucleic Acids',
        hindi: 'न्यूक्लिक अम्ल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Nucleotide = base + sugar + phosphate. Bases:',
        hindi: 'न्यूक्लियोटाइड = क्षार + शर्करा + फॉस्फेट। आधार:',
            },
            {
              english: '• Purines: Adenine (A), Guanine (G).',
        hindi: '• प्यूरीन: एडेनिन (ए), गुआनिन (जी)।',
            },
            {
              english: '• Pyrimidines: Cytosine (C), Thymine (T — DNA only), Uracil (U — RNA only).',
        hindi: '• पाइरिमिडाइन्स: साइटोसिन (सी), थाइमिन (केवल टी-डीएनए), यूरेसिल (केवल यू-आरएनए)।',
            },
            {
              english: 'Sugar: deoxyribose (DNA) vs ribose (RNA). Backbone: 3\'-5\' phosphodiester.',
        hindi: 'चीनी: डीऑक्सीराइबोज़ (डीएनए) बनाम राइबोज़ (आरएनए)। रीढ़ की हड्डी: 3\'-5\' फॉस्फोडिएस्टर।',
            },
            {
              english: '**Watson-Crick** (1953) — DNA double helix; antiparallel; A=T (2 H-bonds), G≡C (3 H-bonds).',
        hindi: '**वाटसन-क्रिक** (1953) - डीएनए डबल हेलिक्स; प्रतिसमानांतर; A=T (2 H-bonds), G≡C (3 H-बॉन्ड)।',
            },
            {
              english: 'RNA — single-stranded; mRNA, tRNA, rRNA. Replication, transcription, translation (genetic code, codons).',
        hindi: 'आरएनए - एकल-फंसे; एमआरएनए, टीआरएनए, आरआरएनए। प्रतिकृति, प्रतिलेखन, अनुवाद (आनुवंशिक कोड, कोडन)।',
            },
            {
              english: 'DNA stores genetic info (in chromosomes); RNA carries it for protein synthesis.',
        hindi: 'डीएनए आनुवंशिक जानकारी संग्रहीत करता है (गुणसूत्रों में); आरएनए इसे प्रोटीन संश्लेषण के लिए ले जाता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\alpha\\text{-D-glucose} \\rightleftharpoons \\beta\\text{-D-glucose}',
      desc: {
        english: 'Mutarotation',
        hindi: 'उत्परिवर्तन',
      },
    },
    {
      latex: 'A=T,\\ G\\equiv C',
      desc: {
        english: 'DNA base pairing',
        hindi: 'डीएनए बेस पेयरिंग',
      },
    },
    {
      latex: 'C_{12}H_{22}O_{11} + H_2O \\to C_6H_{12}O_6 + C_6H_{12}O_6',
      desc: {
        english: 'Sucrose hydrolysis',
        hindi: 'सुक्रोज हाइड्रोलिसिस',
      },
    },
    {
      latex: '\\text{Zwitterion: } H_3N^+\\text{-CHR-COO}^-',
      desc: {
        english: 'Amino acid in solid',
        hindi: 'ठोस में अमीनो एसिड',
      },
    },
    {
      latex: 'pI = \\tfrac{1}{2}(pK_{a1} + pK_{a2})',
      desc: {
        english: 'Isoelectric point (neutral side chain)',
        hindi: 'आइसोइलेक्ट्रिक बिंदु (तटस्थ पक्ष श्रृंखला)',
      },
    },
    {
      latex: 'C_6H_{12}O_6 + Br_2/H_2O \\to \\text{gluconic acid}',
      desc: {
        english: 'Mild oxidation of glucose',
        hindi: 'ग्लूकोज का हल्का ऑक्सीकरण',
      },
    },
    {
      latex: '\\text{Sucrose: } [\\alpha] = +66.5° \\to -39.7°',
      desc: {
        english: 'Inversion',
        hindi: 'उलटा',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Sugars',
      problem: {
        english: 'Which is a non-reducing sugar?',
        hindi: 'कम न होने वाली चीनी कौन सी है?',
      },
      answer: {
        english: 'Sucrose',
        hindi: 'सुक्रोज',
      },
      solution: {
        english: 'Both anomeric C in glycosidic linkage.',
        hindi: 'ग्लाइकोसिडिक लिंकेज में दोनों एनोमेरिक सी।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Vitamin',
      problem: {
        english: 'Deficiency of vitamin C causes:',
        hindi: 'विटामिन सी की कमी के कारण:',
      },
      answer: {
        english: 'Scurvy',
        hindi: 'स्कर्वी',
      },
      solution: {
        english: 'Ascorbic acid required for collagen.',
        hindi: 'कोलेजन के लिए एस्कॉर्बिक एसिड आवश्यक है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'DNA',
      problem: {
        english: 'In DNA, adenine pairs with:',
        hindi: 'डीएनए में, एडेनिन इसके साथ जुड़ता है:',
      },
      answer: {
        english: 'Thymine (2 H-bonds)',
        hindi: 'थाइमिन (2 एच-बॉन्ड)',
      },
      solution: {
        english: 'Watson-Crick.',
        hindi: 'वाटसन-क्रिक।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Glucose',
      problem: {
        english: 'Glucose does not react with NaHSO₃ because:',
        hindi: 'ग्लूकोज NaHSO₃ के साथ प्रतिक्रिया नहीं करता है क्योंकि:',
      },
      answer: {
        english: 'CHO is locked as cyclic hemiacetal',
        hindi: 'CHO को चक्रीय हेमिसिएटल के रूप में बंद कर दिया गया है',
      },
      solution: {
        english: 'Most exists as pyranose form.',
        hindi: 'अधिकांश पायरानोज़ रूप में मौजूद हैं।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Protein',
      problem: {
        english: 'Quaternary structure example:',
        hindi: 'चतुर्धातुक संरचना उदाहरण:',
      },
      answer: {
        english: 'Haemoglobin (2α + 2β subunits)',
        hindi: 'हीमोग्लोबिन (2α + 2β सबयूनिट)',
      },
      solution: {
        english: 'Multi-subunit assembly.',
        hindi: 'मल्टी-सबयूनिट असेंबली।',
      },
    },
  ],
  next: {
    slug: 'polymers',
    title: 'Polymers',
  },
};

export default chapter;
